import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Define the API response structure
interface LoginApiResponse {
  resultcode: string;
  message?: string;
  response?: {
    user_token?: string;
    jsonwebtoken?: string;
    was_server?: string;
    was_port?: string;
    gps_server?: string;
    gps_port?: string;
    gps_tracking_server?: string;
    gps_tracking_port?: string;
    geo_fence_server?: string;
    geo_fence_port?: string;
    userType?: string;
    subscribeinfo?: SubscribeInfo;
    latestlogindt?: string;
    lastMobileOS?: string;
    lastAppVer?: string;
    lastWebVer?: string;
    'logged in list'?: {
      ids?: Array<{
        id: string;
        mobile_name: string;
        mobile_os_type: string;
      }>;
    };
  };
  // Support for properties directly on root object
  user_token?: string;
  jwt_token?: string;
  jsonwebtoken?: string;
}

interface ServerConfig {
  was_server: string;
  was_port: string;
  gps_server: string;
  gps_port: string;
  gps_tracking_server: string;
  gps_tracking_port: string;
  geo_fence_server: string;
  geo_fence_port: string;
}

interface SubscribeInfo {
  servicePlanID: number;
  servicePlanName: string;
  cameraLimit: number;
  paymentMethod: string;
  cancelled: number;
  cancelDT: string;
  startOfMonthDT: string;
  endOfMonthDT: string;
  tradeType: string;
  registered: boolean;
  firstTime: boolean;
  masterEmail: string;
  parentEmail: string;
  userEmail: string;
  userType: string;
}

interface AuthState {
  isAuthenticated: boolean;
  userToken: string | null;
  jwtToken: string | null;
  serverConfig: ServerConfig | null;
  userType: string | null;
  subscribeInfo: SubscribeInfo | null;
  latestLoginInfo: {
    timestamp: string;
    mobileOS: string;
    appVersion: string;
    webVersion: string;
    duplicateLogins?: Array<{
      id: string;
      mobile_name: string;
      mobile_os_type: string;
    }>;
  } | null;
  error: string | null;
  setAuth: (data: LoginApiResponse) => void;
  clearAuth: () => void;
  setError: (error: string | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      userToken: null,
      jwtToken: null,
      serverConfig: null,
      userType: null,
      subscribeInfo: null,
      latestLoginInfo: null,
      error: null,
      setAuth: (data) => {
        if (data.resultcode === 'BC_ERR_OK') {
          // Handle both nested response structure and direct properties
          const responseData = data.response || {};
          
          // Handle duplicate logins if present
          const duplicateLogins = responseData['logged in list']?.ids;
          
          set({
            isAuthenticated: true,
            userToken: responseData.user_token ?? data.user_token ?? null,
            jwtToken: responseData.jsonwebtoken ?? data.jsonwebtoken ?? data.jwt_token ?? null,
            serverConfig: {
              was_server: responseData.was_server ?? '',
              was_port: responseData.was_port ?? '',
              gps_server: responseData.gps_server ?? '',
              gps_port: responseData.gps_port ?? '',
              gps_tracking_server: responseData.gps_tracking_server ?? '',
              gps_tracking_port: responseData.gps_tracking_port ?? '',
              geo_fence_server: responseData.geo_fence_server ?? '',
              geo_fence_port: responseData.geo_fence_port ?? '',
            },
            userType: responseData.userType ?? null,
            subscribeInfo: responseData.subscribeinfo ?? null,
            latestLoginInfo: {
              timestamp: responseData.latestlogindt ?? '',
              mobileOS: responseData.lastMobileOS ?? '',
              appVersion: responseData.lastAppVer ?? '',
              webVersion: responseData.lastWebVer ?? '',
              duplicateLogins: duplicateLogins,
            },
            error: null,
          });
        } else {
          set({
            error: data.message ?? 'Login failed',
            isAuthenticated: false,
          });
        }
      },
      clearAuth: () => {
        set({
          isAuthenticated: false,
          userToken: null,
          jwtToken: null,
          serverConfig: null,
          userType: null,
          subscribeInfo: null,
          latestLoginInfo: null,
          error: null,
        });
      },
      setError: (error) => set({ error }),
    }),
    {
      name: 'auth-storage',
      skipHydration: false,
    }
  )
);
